import {useEffect, useRef, useState} from 'react'
import {Link as RouterLink, useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import type {UserResponse} from '../../common/api/generated/model/userResponse'
import {useAuth} from '../auth/useAuth'
import UserAvatar from '../components/userAvatar/UserAvatar'
import {computeInitials} from '../components/userAvatar/initials'
import './NavAvatar.css'

interface NavAvatarProps {
  me: UserResponse
  active?: boolean
}

const NavAvatar = ({me, active}: NavAvatarProps) => {
  const {t} = useTranslation()
  const {signOut} = useAuth()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const initials = computeInitials(me.firstName, me.lastName, me.email)
  const displayName = [me.firstName, me.lastName].filter(Boolean).join(' ').trim() || me.email

  const handleSignOut = async () => {
    setOpen(false)
    await signOut()
    navigate('/login')
  }

  return (
    <div className="nav-avatar-wrap" ref={wrapRef}>
      <UserAvatar
        seed={me.id}
        imageUrl={me.avatarUrl}
        initials={initials}
        size={32}
        active={active}
        onClick={() => setOpen((o) => !o)}
        ariaLabel={t('account.headerMenu.label')}
        ariaExpanded={open}
        ariaHasPopup="menu"
      />

      {open && (
        <div className="nav-avatar-menu" role="menu">
          <div className="nav-avatar-id">
            <div className="nav-avatar-id-name">{displayName}</div>
            <div className="nav-avatar-id-email">{me.email}</div>
          </div>
          <RouterLink
            to="/account"
            className={`nav-avatar-item ${active ? 'is-active' : ''}`}
            role="menuitem"
            onClick={() => setOpen(false)}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="8" r="4" />
              <path d="M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6" />
            </svg>
            {t('account.headerMenu.accountSettings')}
          </RouterLink>
          <button type="button" className="nav-avatar-item nav-avatar-signout" role="menuitem" onClick={handleSignOut}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M14 4h4a2 2 0 012 2v12a2 2 0 01-2 2h-4" />
              <path d="M9 12h11" />
              <path d="M16 8l4 4-4 4" />
            </svg>
            {t('account.headerMenu.signOut')}
          </button>
        </div>
      )}
    </div>
  )
}

export default NavAvatar
